import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import DictationPreview from "./DictationPreview";

const pickMime = () => ["audio/webm", "audio/mp4", "audio/ogg"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";

export default function DictationButton({ dossierId, target, onApplied, compact, testid = "dictation-btn" }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | transcribing | analyzing | preview | applying
  const [result, setResult] = useState({ transcript: "", changes: [] });
  const recRef = useRef(null);
  const chunks = useRef([]);

  const analyze = async (text) => {
    setPhase("analyzing");
    try {
      const { data } = await api.post(`/dossiers/${dossierId}/dictation/parse`, { target, text });
      setResult(data);
      setPhase("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse impossible");
      setResult({ transcript: text, changes: [] });
      setPhase("preview");
    }
  };

  const onStop = async (stream, mime) => {
    stream.getTracks().forEach((t) => t.stop());
    const blob = new Blob(chunks.current, { type: mime || "audio/webm" });
    chunks.current = [];
    if (!blob.size) { setPhase("idle"); return toast.error("Aucun audio capturé"); }
    setPhase("transcribing");
    try {
      const fd = new FormData();
      fd.append("audio", blob, mime.includes("mp4") ? "dictee.m4a" : mime.includes("ogg") ? "dictee.ogg" : "dictee.webm");
      const { data } = await api.post("/assistant/transcribe", fd);
      const text = (data?.text || "").trim();
      if (!text) { setPhase("idle"); return toast.warning("Rien d'audible n'a été détecté"); }
      await analyze(text);
    } catch (e) {
      setPhase("idle");
      toast.error(e?.response?.data?.detail || "Transcription impossible");
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setResult({ transcript: "", changes: [] });
      const mime = pickMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mr.ondataavailable = (e) => e.data?.size && chunks.current.push(e.data);
      mr.onstop = () => onStop(stream, mr.mimeType || mime);
      recRef.current = mr;
      mr.start();
      setPhase("recording");
    } catch {
      toast.error("Microphone inaccessible — autorisez le micro dans le navigateur");
    }
  };

  const apply = async (selected) => {
    setPhase("applying");
    try {
      const { data } = await api.post(`/dossiers/${dossierId}/dictation/apply`, { target, changes: selected.map(({ kind, key, new: v }) => ({ kind, key, new: v })) });
      toast.success(`${data.applied} modification(s) enregistrée(s) par dictée`);
      setPhase("idle");
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
      setPhase("preview");
    }
  };

  const recording = phase === "recording";
  const working = phase === "transcribing" || phase === "analyzing";
  const label = recording ? "Arrêter" : phase === "transcribing" ? "Transcription…" : phase === "analyzing" ? "Analyse IA…" : "Dicter";
  return (
    <>
      <button
        onClick={recording ? () => recRef.current?.stop() : start}
        disabled={working || phase === "applying"}
        className={`relative inline-flex items-center gap-2 rounded-full font-medium transition-colors shadow-sm disabled:opacity-70 ${compact ? "px-3 py-1 text-xs" : "px-4 py-2 text-sm"} ${
          recording ? "bg-red-600 text-white hover:bg-red-700" : "bg-norea-primary text-white hover:bg-norea-primary/90"
        }`}
        title={recording ? "Cliquez pour arrêter et analyser" : "Parlez : l'IA remplit les champs à votre place"}
        data-testid={testid}
      >
        {recording && <span className="absolute inset-0 rounded-full animate-ping bg-red-500/40" aria-hidden="true" />}
        {working ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-3.5 h-3.5 fill-current" /> : <Mic className="w-4 h-4" />}
        <span className="relative">{label}</span>
      </button>
      <DictationPreview
        open={phase === "preview" || phase === "applying" || (phase === "analyzing" && !!result.transcript)}
        transcript={result.transcript}
        changes={result.changes}
        busy={phase === "analyzing" || phase === "applying"}
        onReanalyze={analyze}
        onApply={apply}
        onClose={() => setPhase("idle")}
      />
    </>
  );
}
